import { DT, MAX_BULLET_SPEED, PF_H } from './constants';

/* ═══════════════════════════════════════════════════════════════════════════
   Les constantes de sensation.

   Valeurs issues du plan (PLAN.md §6.2) et validées au J0 sur le prototype
   jetable. Elles se FIGENT au J2, accompagnées des assertions CI ci-dessous.

   Règle : ces nombres ne se devinent pas et ne se discutent pas en réunion.
   Ils se trouvent en jouant, puis on les défend par des assertions pour qu'ils
   ne dérivent pas au fil des mois.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Escorte ──────────────────────────────────────────────────────────────
   Emplacements des drones, en unités monde relatives au vaisseau.

   ⚠️ Ce tableau appartient à la SIMULATION et non à la vue, alors qu'il décrit
   des positions. La raison est qu'il détermine d'où PARTENT les projectiles
   supplémentaires : si la vue posait les drones ailleurs que là où le tir naît,
   on retomberait sur le défaut que le rapport hurtbox/visuel existe pour
   supprimer — des tirs qui semblent sortir du vide.

   L'origine du problème est un retour de jeu : « je prends un drone
   supplémentaire et je ne vois aucun compagnon apparaître ». La carte ajoutait
   un projectile de plus dans une gerbe déjà serrée, ce qui ne se lit pas comme
   une escorte. Les tirs au-delà des deux canons de fuselage partent désormais
   d'ici, et la vue y dessine un appareil.

   Ils traînent VERS L'ARRIÈRE (y négatif) et s'écartent : une formation en V
   se lit d'un coup d'œil, une rangée alignée non. */
export const DRONE_SLOTS: ReadonlyArray<{ readonly x: number; readonly y: number }> = [
  { x: -46, y: -8 },
  { x: 46, y: -8 },
  { x: -80, y: -22 },
  { x: 80, y: -22 },
  { x: -112, y: -36 },
  { x: 112, y: -36 },
  { x: -142, y: -50 },
  { x: 142, y: -50 },
];

/** Canons de fuselage : les deux premiers projectiles, toujours présents. */
export const HULL_GUNS = 2;

/* ── Répartition du tir entre fuselage et escorte ────────────────────────────
   Un drone est un appareil d'appui : son tir se lit comme un CALIBRE PLUS
   LÉGER — plus petit, plus rapide, moins de mordant par impact. C'est un retour
   de jeu (« les drones tirent la même chose que moi »).

   ⚠️ Le DPS TOTAL est conservé, et ce n'est pas un détail : la courbe de
   puissance et les PV des trois boss ont été calibrés sur `cadence × nombre ×
   dégâts`. Ce que les drones perdent, le fuselage le reprend, exactement. Une
   simple baisse des dégâts de drone aurait fait sombrer toute la campagne
   sous sa cible sans qu'aucun chiffre de contenu ne bouge.

   Le drone touche `DRONE_DMG_FRAC` de la part moyenne ; les deux canons de
   fuselage se partagent le reste. La somme des dégâts d'une volée vaut donc
   toujours `bulletCount × damage`, à la flottante près. */
export const DRONE_DMG_FRAC = 0.62;
/** Rayon du tir de drone, en fraction de celui du fuselage. Plus fin. */
export const DRONE_BULLET_RADIUS_FRAC = 0.62;
/** Vitesse du tir de drone, en fraction de celle du fuselage. Plus rapide :
 *  un calibre léger part plus vite, et l'écart se lit avant la couleur. */
export const DRONE_BULLET_SPEED_FRAC = 1.18;

/** Rayon de collision d'un drone, en unités.
 *
 *  ⚠️ Nettement plus GROS que la hitbox du joueur (9 u), et c'est délibéré. Le
 *  point de hitbox minuscule est le contrat central du jeu : il rend un rideau
 *  franchissable et il est toujours affiché. Les drones ne bénéficient pas de
 *  cette faveur — ils sont des appareils à part entière, on les voit, on les
 *  perd si on les laisse dans le danger. Leur donner 9 u les aurait rendus
 *  quasiment invulnérables et la contrainte n'aurait jamais existé. */
export const DRONE_RADIUS = 13;

/** Durée d'indisponibilité d'un drone touché, en secondes.
 *
 *  ⚠️ Un drone touché est MIS HORS SERVICE, jamais détruit définitivement.
 *  La différence est toute la conception : une destruction permanente ferait de
 *  « Drone d'escorte » une carte piège — on paie une amélioration épique et on
 *  la perd dix secondes plus tard, sans recours jusqu'à la fin de la partie.
 *
 *  Six secondes coûtent une part réelle du débit sans casser le build. Le
 *  joueur qui protège son escorte tire plus fort que celui qui la traîne dans
 *  le rideau, ce qui est exactement la tension recherchée. */
export const DRONE_DOWN_TIME = 6;

export const FEEL = {
  /** u/s. LA valeur la plus structurante du jeu : tout le bestiaire et toutes
   *  les vitesses de projectiles en découlent. Fourchette explorée au J0 :
   *  420-700. À 480, traverser la largeur prend 1,88 s — jeu posé. */
  playerSpeed: 480,
  /** Le focus n'est jamais un simple ralentissement : chaque arme change aussi
   *  de comportement (PLAN.md §6.3). */
  focusMul: 0.66,

  /** Constantes de temps de l'inertie, en secondes. 25 ms à l'accélération et
   *  14 ms à la décélération donnent 95 % de la vitesse en 75 ms et un arrêt
   *  en 42 ms : le vaisseau répond sèchement sans être rigide. */
  tauAccel: 0.025,
  tauDecel: 0.014,

  /** Rayon de la hitbox joueur, en unités. 9 u sur 1 200 de haut = 1,5 % de la
   *  hauteur. Minuscule et TOUJOURS affichée : c'est ce qui rend un rideau de
   *  balles franchissable et lisible plutôt qu'arbitraire. */
  hitboxRadius: 9,
  /** 5 × la hitbox. Frôler rapporte, et surtout permet de tenir sa chaîne
   *  pendant les phases d'esquive pure d'un boss (PLAN.md §6.8). */
  grazeRadius: 45,

  /** Invulnérabilité après un coup, en secondes. */
  iframes: 1.4,
  /** Rayon de purge des balles au moment de l'impact. Sans lui, on meurt deux
   *  fois de suite sur le même rideau et ça passe pour de l'injustice. */
  deathClearRadius: 220,
  /** Délai avant réapparition. Le contrôle est rendu IMMÉDIATEMENT à la
   *  réapparition — jamais d'animation qui prive le joueur de ses commandes. */
  respawnDelay: 0.35,

  /** Cadence de l'arme de départ (VULCAN) : 10 volées/s × 2 projectiles ×
   *  10 dégâts = 200 DPS. C'est l'unité de mesure de tout l'équilibrage. */
  fireRate: 10,
  playerBulletSpeed: 1800,
  playerBulletDamage: 10,
  /** Hitbox du tir joueur à 175 % du sprite. Même logique que la hurtbox
   *  ennemie : l'asymétrie se ressent comme de la puissance. */
  playerBulletRadius: 14,

  enemyBulletSpeed: 450,
  enemyBulletRadius: 11,

  /** Rayon d'aimantation des ramassables. Au-delà, ils dérivent librement. */
  pickupMagnetRadius: 260,
  /** Rayon de ramassage effectif — généreux (PLAN.md §6.2 : « 300 % »).
   *  Ramasser ne doit jamais demander de précision : c'est une récompense, pas
   *  une épreuve d'adresse. */
  pickupGrabRadius: 46,
  /** Durée de vie maximale d'un ramassable, en secondes. Filet de sécurité
   *  contre toute trajectoire qui ne se terminerait ni par un ramassage ni par
   *  une sortie d'écran. */
  pickupTtl: 12,

  /* ⚠️ `sealRadius` a été RETIRÉ d'ici. Le rayon de scellement dépend désormais
     de la difficulté (140 / 100 / 70 u) et vit dans `src/sim/difficulty.ts`.

     Le laisser en double aurait produit la divergence habituelle : deux
     constantes décrivant la même notion, dont une seule est lue par le moteur.
     Quiconque aurait réglé celle-ci aurait constaté qu'elle ne change rien, ce
     qui est la pire forme de piège — le code semble répondre, et il ignore. */
  /** Au-delà de cette abscisse, tout tir visé est biaisé vers le centre : le
   *  joueur ne peut jamais être cloué contre un bord. */
  edgeCeasefireX: 380,
  edgeCeasefireBiasDeg: 12,

  /* ── Bombes et death-bomb (PLAN.md §6.3) ─────────────────────────────── */
  bombsAtStart: 2,
  bombsMax: 4,
  /** Invulnérabilité accordée par une bombe. */
  bombIframes: 1.6,
  /** Rayon d'effacement des projectiles. */
  bombClearRadius: 900,

  /** ⚠️ La fenêtre de death-bomb : 150 ms (18 ticks) après l'impact pendant
   *  lesquelles appuyer sur bombe annule la perte de vie.
   *
   *  Elle n'est écrite nulle part dans l'interface, et c'est volontaire. Son
   *  rôle n'est pas d'être connue mais d'être ressentie : elle transforme des
   *  dizaines de morts « j'ai réagi trop tard » en morts « j'ai réagi juste à
   *  temps ». C'est l'un des réglages les plus rentables du genre, et l'un des
   *  plus systématiquement oubliés. */
  deathBombWindow: 0.15,

  /* ── Impact (PLAN.md §6.2) ───────────────────────────────────────────── */
  /** ⚠️ ZÉRO sur le popcorn, et c'est une règle sacrée. À 15 kills/seconde,
   *  30 ms de gel par mort gèlent 45 % du temps de jeu et font exploser la
   *  latence perçue. Le petit ennemi ne doit rien peser. */
  hitstopPopcorn: 0,
  hitstopStandard: 0.022,
  hitstopHeavy: 0.06,
  hitstopPlayerHit: 0.09,

  /** Fraction de la hauteur du terrain. Plafonné à 1 % : au-delà, la secousse
   *  masque les projectiles, et cacher le danger est un bug de conception dans
   *  un jeu où l'on esquive au pixel. */
  shakeAmplitude: 0.008,
  /** Décroissance LINÉAIRE du trauma, par seconde. */
  shakeDecay: 1.8,
  /** Roulis maximal, en degrés. */
  shakeRollDeg: 0.45,

  /** Apports de trauma par événement. Le tir du joueur en est absent : il
   *  utilise le recul du mesh, pas la caméra. Secouer l'écran dix fois par
   *  seconde rendrait le jeu illisible en permanence. */
  traumaPopcorn: 0.04,
  traumaStandard: 0.11,
  traumaHeavy: 0.26,
  traumaPlayerHit: 1.0,

  /** Recul du vaisseau à chaque volée, en unités. Purement visuel. */
  recoil: 6,

  /* ── Recul des ENNEMIS à l'impact (retour de jeu) ──────────────────────
     Un tir doit se RESSENTIR : la piétaille se fait bousculer, un lourd bronche
     à peine, un boss ne bouge pas. Le « poids » n'a pas de champ dédié — les
     points de vie MAXIMAUX en tiennent lieu, et c'est même mieux qu'une masse
     inventée : un ennemi coriace est lourd par définition. */
  /** Décalage de recul par point de dégât et par PV max, en unités.
   *  Calibré pour ~12 u sur une Mite (30 PV, tir de 10) et ~0,02 u sur un
   *  noyau de boss (18 000 PV) — soit rien. */
  kbStrength: 36,
  /** Plafond du décalage cumulé, en unités. Un ennemi arrosé en continu
   *  atteint un régime stationnaire ; ce plafond l'empêche de s'envoler hors du
   *  cadre pendant qu'on le mitraille. */
  kbMaxOffset: 28,

  /* ── Télégraphes (PLAN.md §6.5) ──────────────────────────────────────── */
  /** Durée pendant laquelle un ennemi qui apparaît est visible mais inoffensif
   *  ET intouchable. Le joueur a le temps de le catégoriser avant d'avoir à
   *  réagir — sans quoi la mort qui suit passe pour de l'arbitraire. */
  telegraphPopcorn: 0.7,
  telegraphHeavy: 1.1,
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   Assertions de conception (PLAN.md §3.6).

   Ce ne sont pas des tests de code : ce sont des garde-fous contre une dérive
   de DESIGN. Elles tournent en CI, et leur seul rôle est de faire échouer le
   build le jour où quelqu'un — dans six mois, sous pression — remonte la
   hitbox « juste un peu » pour faire passer un pattern trop dur.
   ═══════════════════════════════════════════════════════════════════════════ */
export interface Invariant {
  readonly name: string;
  readonly ok: boolean;
  readonly detail: string;
}

export function checkFeelInvariants(): Invariant[] {
  const out: Invariant[] = [];

  const hitRatio = FEEL.hitboxRadius / (PF_H / 2);
  out.push({
    name: 'hitbox ≤ 1,6 % de la demi-hauteur',
    ok: hitRatio <= 0.016,
    detail: `${hitRatio.toFixed(4)} (max 0.0160)`,
  });

  // Distance de dérapage : intégrale de la décélération exponentielle, soit
  // v · tau. Si elle dépasse la hitbox, le joueur meurt « après avoir lâché la
  // touche » — l'une des sensations d'injustice les plus fréquentes du genre.
  const skid = FEEL.playerSpeed * FEEL.tauDecel;
  out.push({
    name: 'dérapage < 1,5 × hitbox',
    ok: skid < FEEL.hitboxRadius * 1.5,
    detail: `${skid.toFixed(1)} u (max ${(FEEL.hitboxRadius * 1.5).toFixed(1)} u)`,
  });

  // Une balle doit mettre au moins 0,80 s à traverser l'écran : le temps de
  // réaction visuel humain est d'environ 250 ms, ce qui laisse une marge de 3×.
  const crossing = PF_H / FEEL.enemyBulletSpeed;
  out.push({
    name: 'traversée d’écran ≥ 0,80 s',
    ok: crossing >= 0.8,
    detail: `${crossing.toFixed(2)} s`,
  });

  out.push({
    name: 'graze strictement au-delà de la hitbox',
    ok: FEEL.grazeRadius > FEEL.hitboxRadius * 2,
    detail: `${FEEL.grazeRadius} u vs hitbox ${FEEL.hitboxRadius} u`,
  });

  out.push({
    name: 'tir joueur plus rapide que tir ennemi (hiérarchie de lecture)',
    ok: FEEL.playerBulletSpeed > FEEL.enemyBulletSpeed * 2,
    detail: `${FEEL.playerBulletSpeed} vs ${FEEL.enemyBulletSpeed} u/s`,
  });

  /* Il y avait ici un invariant « tir joueur : pas de tunnel sans balayage »,
     qui comparait le déplacement par tick au plus petit rayon de hurtbox pour
     autoriser un test de collision ponctuel. Il était faux sur deux points :
     il ignorait la vitesse de FERMETURE — l'ennemi se déplace aussi — et il ne
     couvrait pas les impacts rasants, où la fenêtre de recouvrement tend vers
     zéro quelle que soit la marge. Les tirs du joueur sont désormais balayés
     comme ceux des ennemis ; l'invariant est donc sans objet et retiré plutôt
     que réécrit. On ne borne pas un problème qu'on peut supprimer. */

  // Le ramassage doit rester nettement plus généreux que la hitbox : ramasser
  // est une récompense, pas une épreuve d'adresse.
  out.push({
    name: 'ramassage plus généreux que la hitbox',
    ok: FEEL.pickupGrabRadius > FEEL.hitboxRadius * 3,
    detail: `${FEEL.pickupGrabRadius} u vs hitbox ${FEEL.hitboxRadius} u`,
  });

  // Le rayon de ramassage doit être strictement inclus dans le rayon
  // d'aimantation, sinon un objet peut être happé sans jamais pouvoir être
  // atteint — c'était la mécanique exacte de la fuite du pool.
  out.push({
    name: 'ramassage strictement inclus dans l’aimantation',
    ok: FEEL.pickupGrabRadius < FEEL.pickupMagnetRadius,
    detail: `${FEEL.pickupGrabRadius} u < ${FEEL.pickupMagnetRadius} u`,
  });

  /* ── Assertions de game feel devenues applicables au J2 ────────────────── */

  // ⚠️ Règle sacrée du §6.2. À 15 kills/seconde, le moindre gel sur le popcorn
  // gèlerait près de la moitié du temps de jeu. Cette assertion existe parce
  // que « juste 10 ms, ça se sentirait mieux » est une tentation permanente.
  out.push({
    name: 'hitstop nul sur le popcorn',
    ok: FEEL.hitstopPopcorn === 0,
    detail: `${FEEL.hitstopPopcorn * 1000} ms (doit être 0)`,
  });

  // Au-delà de 1 % de la hauteur, la secousse masque les projectiles. Cacher
  // le danger dans un jeu où l'on esquive au pixel est un bug de conception,
  // pas un effet.
  out.push({
    name: 'amplitude de secousse ≤ 1 % de la hauteur',
    ok: FEEL.shakeAmplitude <= 0.01,
    detail: `${(FEEL.shakeAmplitude * 100).toFixed(2)} % (max 1,00 %)`,
  });

  // Un gel plus long que la fenêtre de death-bomb la rendrait injouable : le
  // joueur n'aurait pas de tick pour appuyer.
  out.push({
    name: 'fenêtre de death-bomb plus longue que le gel d’impact',
    ok: FEEL.deathBombWindow > FEEL.hitstopPlayerHit,
    detail: `${FEEL.deathBombWindow * 1000} ms de fenêtre vs ${FEEL.hitstopPlayerHit * 1000} ms de gel`,
  });

  // Et elle doit laisser plusieurs ticks de réaction, pas un seul.
  out.push({
    name: 'death-bomb : au moins 10 ticks pour réagir',
    ok: FEEL.deathBombWindow / DT >= 10,
    detail: `${Math.round(FEEL.deathBombWindow / DT)} ticks`,
  });

  // Une balle ne doit jamais dépasser le plafond de vitesse du plan : au-delà,
  // la traversée d'écran passe sous 0,80 s et le jeu devient de la
  // mémorisation plutôt que de la lecture.
  out.push({
    name: 'vitesse des projectiles sous le plafond',
    ok: FEEL.enemyBulletSpeed <= MAX_BULLET_SPEED,
    detail: `${FEEL.enemyBulletSpeed} u/s (max ${MAX_BULLET_SPEED})`,
  });

  return out;
}
